import { useEffect, useState } from "react";
import { Users, ExternalLink, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "../lib/api";
import { fmtMoney, displayTicker } from "../lib/format";

const REC_COLORS = {
  Buy: "#4E6E5D",
  Outperform: "#7C8E73",
  Hold: "#B8A98C",
  Underperform: "#D49B7C",
  Sell: "#C58A6B",
};

export default function BrokerCallsPanel({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/stocks/${encodeURIComponent(ticker)}/broker-calls`, { timeout: 60000 });
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (loading) {
    return (
      <section className="bg-white border border-line rounded-sm p-6 lg:p-8 mb-6 animate-pulse" data-testid="broker-calls-panel-loading">
        <div className="h-6 bg-bone-100 w-1/3 mb-4 rounded-sm" />
        <div className="h-40 bg-bone-100 rounded-sm" />
      </section>
    );
  }

  const mc = data?.moneycontrol;
  const yahoo = data?.yahoo;
  const cur = data?.currency || "INR";
  const price = data?.current_price;
  const hasMc = mc && Array.isArray(mc.trajectory) && mc.trajectory.length > 0;
  const hasYahoo = yahoo && (yahoo.strong_buy != null || yahoo.buy != null || yahoo.hold != null);

  if (!hasMc && !hasYahoo) return null;

  // Compute latest-month delta vs first-month for the "trend" headline
  let trendBlurb = null;
  if (hasMc && mc.trajectory.length >= 2) {
    const first = mc.trajectory[0];
    const last = mc.trajectory[mc.trajectory.length - 1];
    const firstBuy = (first.Buy || 0) + (first.Outperform || 0);
    const lastBuy = (last.Buy || 0) + (last.Outperform || 0);
    const firstSell = (first.Sell || 0) + (first.Underperform || 0);
    const lastSell = (last.Sell || 0) + (last.Underperform || 0);
    const buyDelta = lastBuy - firstBuy;
    const sellDelta = lastSell - firstSell;
    if (buyDelta > 0) {
      trendBlurb = {
        Icon: TrendingUp,
        text: `+${buyDelta} broker${buyDelta > 1 ? "s" : ""} turned bullish over the last ${mc.trajectory.length} months`,
        color: "text-moss",
      };
    } else if (buyDelta < 0) {
      trendBlurb = {
        Icon: TrendingDown,
        text: `${Math.abs(buyDelta)} broker${Math.abs(buyDelta) > 1 ? "s" : ""} downgraded over the last ${mc.trajectory.length} months`,
        color: "text-terracotta",
      };
    } else if (sellDelta > 0) {
      trendBlurb = {
        Icon: TrendingDown,
        text: `Bullish count steady · ${sellDelta} more sell-side calls`,
        color: "text-amber_brand",
      };
    }
  }

  // Calculate upside vs target_mean
  const upsideMean = mc?.target_mean && price ? ((mc.target_mean - price) / price) * 100 : null;

  return (
    <section
      className="bg-white border border-line rounded-sm p-6 lg:p-8 mb-6"
      data-testid="broker-calls-panel"
    >
      <header className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline flex items-center gap-1.5">
            <Users className="w-3 h-3" />— Broker Consensus Trajectory
          </div>
          <h2 className="font-serif-display text-2xl text-stone_brand-dark font-medium mt-1">
            What sell-side analysts are calling
          </h2>
          {trendBlurb && (
            <div className={`flex items-center gap-1.5 mt-2 ${trendBlurb.color}`}>
              <trendBlurb.Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{trendBlurb.text}</span>
            </div>
          )}
        </div>
        {mc?.url && (
          <a
            href={mc.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-stone_brand hover:text-moss"
            data-testid="broker-calls-mc-link"
          >
            View on Moneycontrol <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </header>

      {/* Target price ribbon */}
      {mc && (mc.target_low || mc.target_mean || mc.target_high) && (
        <div className="grid grid-cols-3 gap-px bg-line border border-line rounded-sm overflow-hidden mb-6" data-testid="broker-calls-targets">
          <div className="bg-white p-3 text-center">
            <div className="overline text-[0.6rem] mb-1">Bear case · low</div>
            <div className="font-mono text-base text-terracotta">{fmtMoney(mc.target_low, cur, 0)}</div>
          </div>
          <div className="bg-bone-100 p-3 text-center border-x border-line">
            <div className="overline text-[0.6rem] mb-1">Mean target</div>
            <div className="font-mono text-lg text-stone_brand-dark font-semibold">{fmtMoney(mc.target_mean, cur, 0)}</div>
            {upsideMean !== null && (
              <div className={`text-[0.65rem] font-mono mt-0.5 ${upsideMean >= 0 ? "text-moss" : "text-terracotta"}`}>
                {upsideMean >= 0 ? "+" : ""}{upsideMean.toFixed(1)}% from ₹{price?.toFixed(2)}
              </div>
            )}
          </div>
          <div className="bg-white p-3 text-center">
            <div className="overline text-[0.6rem] mb-1">Bull case · high</div>
            <div className="font-mono text-base text-moss">{fmtMoney(mc.target_high, cur, 0)}</div>
          </div>
        </div>
      )}

      {/* Trajectory chart */}
      {hasMc && (
        <div className="bg-bone-50 border border-line rounded-sm p-4 mb-5">
          <div className="overline mb-3">— 5-month trajectory · {mc.trajectory[mc.trajectory.length - 1].total} brokers latest</div>
          <div className="h-56" data-testid="broker-calls-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mc.trajectory} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10, fill: "#7B8478", fontFamily: "JetBrains Mono" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E1D8" }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#7B8478", fontFamily: "JetBrains Mono" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E1D8" }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#FFFFFF",
                    border: "1px solid #E5E1D8",
                    borderRadius: 2,
                    fontFamily: "JetBrains Mono",
                    fontSize: 11,
                  }}
                  labelStyle={{ color: "#5C6B64" }}
                />
                <Legend
                  iconType="square"
                  wrapperStyle={{
                    fontFamily: "JetBrains Mono",
                    fontSize: 10,
                    color: "#7B8478",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                />
                {Object.keys(REC_COLORS).map((k) => (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="a"
                    fill={REC_COLORS[k]}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Two-source attribution */}
      <div className="grid sm:grid-cols-2 gap-4">
        {hasMc && (
          <div className="border border-line rounded-sm p-4 bg-white" data-testid="broker-calls-mc-summary">
            <div className="overline mb-2">— Moneycontrol · this month</div>
            <ul className="space-y-1">
              {Object.entries(REC_COLORS).map(([k]) => {
                const v = mc.trajectory[mc.trajectory.length - 1][k] || 0;
                if (!v) return null;
                return (
                  <li key={k} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-stone_brand flex items-center gap-2">
                      <span className="w-2 h-2 rounded-sm" style={{ background: REC_COLORS[k] }} />
                      {k}
                    </span>
                    <span className="font-mono font-semibold text-stone_brand-dark">{v}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {hasYahoo && (
          <div className="border border-line rounded-sm p-4 bg-white" data-testid="broker-calls-yahoo-summary">
            <div className="overline mb-2">— Yahoo Finance · current</div>
            <ul className="space-y-1">
              {[
                ["strong_buy", "Strong Buy", "#4E6E5D"],
                ["buy", "Buy", "#7C8E73"],
                ["hold", "Hold", "#B8A98C"],
                ["sell", "Sell", "#D49B7C"],
                ["strong_sell", "Strong Sell", "#C58A6B"],
              ].map(([key, label, color]) => {
                const v = yahoo[key];
                if (!v) return null;
                return (
                  <li key={key} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-stone_brand flex items-center gap-2">
                      <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
                      {label}
                    </span>
                    <span className="font-mono font-semibold text-stone_brand-dark">{v}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <p className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light mt-4 text-center">
        — {displayTicker(ticker)} · Aggregated free data from Moneycontrol + Yahoo. Per-broker call detail
        is gated behind paid terminals (Bloomberg / Visible Alpha) — not available here.
      </p>
    </section>
  );
}
